/**
 * The queue rail (pane A) — docs/design-handoff/README.md "Queue rail (pane
 * A)" and communicatorv2.3.md §9 "Queue priority". Channel-agnostic from day
 * one: every row is a generic ThreadSummary with a channel type, never a
 * chat-shaped record (RailRow renders voice/sms/chat through one component).
 *
 * This component does not compute a band or a sort order — Task 5 owns
 * computeBand/sortRail in the operations layer. It renders `store.threads`
 * in the order the store already holds it, grouped by the `band` field each
 * thread already carries, in the fixed design order (BAND_ORDER).
 *
 * The one piece of real behaviour that lives here and nowhere else: holding
 * a new arrival behind a pill while the pointer is inside the rail, so
 * nothing ever moves under the cursor (README "Queue rail (pane A)" and
 * communicatorv2.3.md §9 "Guards"). See the arrival-detection effect below.
 */
import { useLayoutEffect, useRef } from 'react'
import type { ThreadSummary } from '@communicator/shared'
import { matchesScope } from '@communicator/shared'
import { ops } from '../../lib/ops'
import type { QueueFilter } from '../../store'
import { useStore } from '../../store'
import { Icon } from '../ui'
import { CallFloor } from './CallFloor'
import { FilterRow } from './FilterRow'
import type { TechOption } from './FilterRow'
import { activateOnKey, BAND_HEADER_TEXT_CLASS, BAND_ICON, BAND_LABEL, BAND_ORDER, heldPillLabel, isOnCallFloor } from './format'
import { RailRow, StackSubRow } from './RailRow'

export function QueueRail() {
  const filter = useStore((s) => s.filter)
  const techFilter = useStore((s) => s.techFilter)
  const techMenu = useStore((s) => s.techMenu)
  const railHover = useStore((s) => s.railHover)
  const held = useStore((s) => s.held)
  const threads = useStore((s) => s.threads)
  const peers = useStore((s) => s.peers)
  const selfId = useStore((s) => s.me?.id ?? null)
  const activeId = useStore((s) => s.activeId)
  const stackOpen = useStore((s) => s.stackOpen)
  const arrived = useStore((s) => s.arrived)
  // Task 7 (multiple SMS lines): see RailRow's own `showLine` prop doc
  // comment — `null`/failed-fetch `store.lines` degrades to the same "don't
  // show" behaviour as zero or one line.
  const showLine = useStore((s) => (s.lines?.length ?? 0) > 1)

  const set = useStore((s) => s.set)
  const selectThread = useStore((s) => s.selectThread)
  const setThreads = useStore((s) => s.setThreads)
  const holdArrival = useStore((s) => s.holdArrival)
  const releaseHeld = useStore((s) => s.releaseHeld)
  const removeThread = useStore((s) => s.removeThread)
  const markArrived = useStore((s) => s.markArrived)
  const clearArrived = useStore((s) => s.clearArrived)

  // --- Arrival detection -------------------------------------------------
  // Diffs `threads` against the ids we last saw. Every genuinely new id,
  // while the pointer is inside the rail, is pulled back out of `threads`
  // and queued onto `held` instead of being allowed to render where the
  // pointer is — `held` is a queue (not a single slot), so a second arrival
  // while one is already held queues behind it rather than bypassing the
  // hold. The held-arrival pill is what shows up there instead. Off-hover,
  // or once already accounted for (already `arrived`, e.g. just placed by
  // releaseHeld), a new id is simply left to render and marked `arrived` so
  // it plays its cc-slide entrance.
  const prevIdsRef = useRef<Set<string> | null>(null)
  const railHoverRef = useRef(railHover)
  railHoverRef.current = railHover
  // Set right before a filter/tech change replaces the whole list — a full
  // re-scope is not a stream of arrivals and must not be held or animated.
  const suppressArrivalDetectionRef = useRef(false)

  useLayoutEffect(() => {
    const currentIds = new Set(threads.map((t) => t.id))
    const prevIds = prevIdsRef.current
    if (prevIds === null) {
      prevIdsRef.current = currentIds
      return
    }
    if (suppressArrivalDetectionRef.current) {
      suppressArrivalDetectionRef.current = false
      prevIdsRef.current = currentIds
      return
    }
    // Only an arrival THIS rail would show. The store now holds the whole
    // floor (the presence tick broadcasts it), so without this every console
    // pulsed for every thread on the system regardless of scope.
    const newOnes = threads.filter(
      (t) => !prevIds.has(t.id) && matchesScope(t, { filter, techId: techFilter, selfId }),
    )
    prevIdsRef.current = currentIds
    if (newOnes.length === 0) return

    const latest = useStore.getState()
    const alreadyArrived = new Set(latest.arrived)
    for (const t of newOnes) {
      if (alreadyArrived.has(t.id)) continue
      if (railHoverRef.current) {
        // Queue it — `held` has no capacity limit, so a second (or third...)
        // arrival while hovering queues behind whatever is already held
        // instead of rendering under the cursor.
        removeThread(t.id)
        holdArrival(t)
      } else {
        markArrived(t.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads])

  function applyFetchedThreads(list: ThreadSummary[]): void {
    suppressArrivalDetectionRef.current = true
    setThreads(list)
  }

  // A filter/tech scope change drops whatever is currently held (CRITICAL 4)
  // rather than carrying it into the new scope: an arrival held under one
  // scope may not even belong in the next one, and if it does, it comes
  // back on its own through the normal path (a fresh fetch or a later SSE
  // event) for the scope it actually belongs to.
  function handleChangeFilter(next: QueueFilter): void {
    set({ filter: next, held: [] })
    ops
      .threads(next, techFilter ?? undefined)
      .then(applyFetchedThreads)
      .catch(() => {})
  }

  function handleToggleTechMenu(): void {
    set({ techMenu: !techMenu })
  }

  function handleSelectTech(techId: string): void {
    set({ techFilter: techId, techMenu: false, held: [] })
    ops
      .threads(filter, techId)
      .then(applyFetchedThreads)
      .catch(() => {})
  }

  function handleBackFromTech(): void {
    set({ techFilter: null, held: [] })
    ops
      .threads(filter, undefined)
      .then(applyFetchedThreads)
      .catch(() => {})
  }

  function handleRailMouseEnter(): void {
    set({ railHover: true })
  }

  function handleRailMouseLeave(): void {
    set({ railHover: false })
    if (useStore.getState().held.length > 0) releaseHeld()
  }

  function handlePlaceArrival(): void {
    releaseHeld()
  }

  function handleToggleStack(id: string): void {
    set({ stackOpen: stackOpen === id ? null : id })
  }

  // Fix round 1 (coordinator review): excludes threads the floor holds, same as
  // `bandGroups` above — a live/ringing call on a shared ticket is the call
  // floor's exclusive business, and must not be counted (or, below, listed)
  // as an ordinary sibling in the stack expansion. Without this, the "N more
  // on #ticket" number and the sub-row list it expands into would disagree
  // with each other, and the live call would surface a second time exactly
  // where the design's own cross-channel example ("Ellen Cobb called about
  // #4821 · Alicia has that call") says it should not.
  // WHAT THIS RAIL SHOWS. The store holds every thread this console has been
  // told about — the fetch is scoped, but the presence tick broadcasts the
  // WHOLE FLOOR to everyone, and the pane needs the open conversation in
  // there regardless of scope to keep receiving live updates.
  //
  // So the tab/tech scope is applied HERE, at render. It is a display rule,
  // and enforcing it by deleting from shared state is what broke the thread
  // pane and made a claimed thread reappear: three separate attempts, each
  // right about its own mechanism, all fighting the same missing distinction.
  //
  // The one thing this must never do is hide the conversation somebody has
  // OPEN from the pane — it does not, because the pane does not read this.
  const visible = threads.filter((t) =>
    matchesScope(t, { filter, techId: techFilter, selfId }),
  )

  function stackCountFor(ticket: string | null): number {
    if (!ticket) return 0
    return visible.filter((t) => t.ticket === ticket && !isOnCallFloor(t)).length
  }

  // THE NUMBER ON THE QUEUE TAB IS THE QUEUE, not a second definition of it.
  //
  // This counted `band <= 3`, while the Queue tab itself selects by state and
  // ownership — so the chip answered a different question from the rows below
  // it and could read 0 with visible work sitting there. It now counts
  // exactly what that tab shows: UNCLAIMED open threads.
  //
  // Counted over `threads`, which holds whatever scope is loaded, so on Mine
  // or a tech's view this is the unclaimed subset of what is on screen rather
  // than a floor-wide figure. That is the honest reading of a number rendered
  // next to the rows it describes.
  //
  // Still excludes floor-held threads (fix round 2, finding 9): a call the
  // floor owns is not a rail row, and counting it made the chip read higher
  // than the number of visible rows.
  const queueCount = visible.filter((t) => t.state !== 'closed' && t.ownerId === null && !isOnCallFloor(t)).length

  // docs/superpowers/specs/2026-08-24-mine-unread-badge-design.md: a claimed
  // thread getting a new message is otherwise invisible until you happen to
  // have it open — the Queue tab correctly excludes claimed threads, so
  // nothing else says "something of yours needs you." Counted over `threads`
  // (not `visible`), same reasoning as `techOptions` above: ownership is a
  // property of the thread, not of whatever tab happens to be selected right
  // now, so switching tabs must not change what the badge reports. `me` not
  // yet resolved → 0, never guess ownership.
  const mineUnread = selfId === null ? 0 : threads.filter((t) => t.ownerId === selfId && t.unread && t.state !== 'closed').length

  // Persistent count of the whole claimed roster (Andrew 2026-08-27: show a
  // number on Mine "just like the queue does", not only a badge that vanishes
  // at zero). Same `threads`-not-`visible` scope and `me`-null → 0 reasoning
  // as mineUnread above: ownership is a property of the thread, not of the
  // selected tab.
  const mineCount = selfId === null ? 0 : threads.filter((t) => t.ownerId === selfId && t.state !== 'closed').length

  const activeTechPeer = peers.find((p) => p.id === techFilter) ?? null
  const techOptions: TechOption[] = peers.map((p) => ({
    id: p.id,
    name: p.name,
    count: threads.filter((t) => t.ownerId === p.id).length,
    note: p.covering ? 'available' : 'not covering',
  }))
  const activeTechLabel = activeTechPeer ? activeTechPeer.name.split(' ')[0]! : 'Techs'
  const viewingTechLabel = activeTechPeer ? `${activeTechPeer.name.toUpperCase()} · READ ACROSS THE TEAM` : ''
  const viewingTechNote = 'Read access is open across the team; ownership only changes if it is taken over.'

  // Task 4 (Tossker repaint), README §3: voice threads the call floor holds
  // are its exclusive business — see `isOnCallFloor`'s doc comment in
  // format.ts for why this filter exists alongside band 1's retirement
  // rather than instead of it.
  //
  // WIDENED from `isLiveVoice` to `isOnCallFloor`. Excluding only ringing
  // and live left the floor's OTHER two kinds — a missed call, and one that
  // ended without being written up — rendering here as well, so the same
  // call occupied a floor card and a rail card at once. Andrew: "they
  // shouldn't appear twice."
  const bandGroups = BAND_ORDER.map((band) => ({
    band,
    items: visible.filter((t) => t.band === band && !isOnCallFloor(t)),
  })).filter((g) => g.items.length > 0)
  // Fix round 2 (finding 8): was `threads.length === 0`, but `bandGroups`
  // above excludes floor-held threads. One live call and nothing else:
  // `threads.length === 1` (not empty) so this never rendered, and
  // `bandGroups` is empty (nothing to iterate) so no rows rendered either —
  // the rail body went blank with no explanation. Keying off `bandGroups`
  // (and `held`, so a queued-but-not-yet-placed arrival doesn't also read
  // as "empty") matches what actually determines whether any row renders.
  const isEmpty = bandGroups.length === 0 && held.length === 0

  return (
    <div className="flex h-full w-[340px] flex-none flex-col border-r border-subtle bg-sidebar">
      {/* Task 3 (Tossker repaint), README §2: pinned above the filter row, so
          it is the first thing a tech sees regardless of which queue filter
          is active. */}
      <CallFloor />
      <FilterRow
        filter={filter}
        onChangeFilter={handleChangeFilter}
        queueCount={queueCount}
        mineCount={mineCount}
        mineUnread={mineUnread}
        techMenuOpen={techMenu}
        onToggleTechMenu={handleToggleTechMenu}
        techOptions={techOptions}
        activeTechId={techFilter}
        activeTechLabel={activeTechLabel}
        onSelectTech={handleSelectTech}
      />

      {activeTechPeer && (
        <div data-testid="viewing-tech-banner" className="flex flex-none items-center gap-[9px] border-b border-subtle bg-info-subtle px-3 py-[9px]">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold tracking-[.07em] text-info">{viewingTechLabel}</div>
            <div className="mt-[2px] text-[11px] leading-[1.4] text-mid">{viewingTechNote}</div>
          </div>
          <div
            data-testid="tech-filter-back"
            role="button"
            tabIndex={0}
            onClick={handleBackFromTech}
            onKeyDown={(e) => activateOnKey(e, handleBackFromTech)}
            className="cursor-pointer whitespace-nowrap rounded-md border border-subtle px-[10px] py-[6px] text-[11px] font-semibold text-mid"
          >
            Back
          </div>
        </div>
      )}


      <div
        data-testid="rail-scroll"
        onMouseEnter={handleRailMouseEnter}
        onMouseLeave={handleRailMouseLeave}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {held.length > 0 && (
          <div
            data-testid="held-arrival-pill"
            role="button"
            tabIndex={0}
            onClick={handlePlaceArrival}
            onKeyDown={(e) => activateOnKey(e, handlePlaceArrival)}
            className="mx-3 mb-[2px] mt-[10px] flex cursor-pointer items-center gap-[9px] rounded-[9px] border border-cc-blue bg-cc-blue-pale px-[11px] py-[9px]"
          >
            <span className="h-[7px] w-[7px] flex-none rounded-full bg-cc-blue" />
            <span className="flex-1 text-[11px] font-semibold text-hi">{heldPillLabel(held)}</span>
            <span className="text-[10px] text-lo">click to place</span>
          </div>
        )}

        {/* Task 4 (Tossker repaint), README §3: each band header is icon + eyebrow
            + count — triangle-alert/danger for breaching, clock for waiting,
            plain (no icon) for not-demanding, in the subtle text colour. */}
        {bandGroups.map(({ band, items }) => (
          <div key={band}>
            <div
              data-testid={`band-header-${band}`}
              className={['flex items-center gap-[5px] px-[13px] pb-[6px] pt-3 text-[10px] font-semibold tracking-[.09em]', BAND_HEADER_TEXT_CLASS[band]].join(
                ' ',
              )}
            >
              {BAND_ICON[band] && <Icon name={BAND_ICON[band]!} size={12} className="flex-none" />}{BAND_LABEL[band]} · {items.length}
            </div>
            {items.map((t) => {
              const count = stackCountFor(t.ticket)
              const isStackOpen = stackOpen === t.id
              return (
                <div key={t.id}>
                  <RailRow
                    thread={t}
                    selected={activeId === t.id}
                    peers={peers}
                    selfId={selfId}
                    showLine={showLine}
                    stackCount={count}
                    stackOpen={isStackOpen}
                    arriving={arrived.includes(t.id)}
                    onSelect={selectThread}
                    onToggleStack={handleToggleStack}
                    onArrivedAnimationEnd={clearArrived}
                  />
                  {isStackOpen &&
                    (() => {
                      // Same exclusion as `stackCountFor`/`bandGroups` above — see the comment on `stackCountFor`.
                      const others = visible.filter((o) => o.ticket === t.ticket && o.id !== t.id && !isOnCallFloor(o))
                      return (
                        <div>
                          <div
                            data-testid={`stack-header-${t.id}`}
                            className="bg-card py-1 pl-10 pr-[13px] text-[9px] font-semibold tracking-[.09em] text-lo"
                          >
                            OTHER COMMS ON {t.ticket}
                          </div>
                          {others.map((o) => (
                            <StackSubRow key={o.id} thread={o} selected={activeId === o.id} onSelect={selectThread} />
                          ))}
                        </div>
                      )
                    })()}
                </div>
              )
            })}
          </div>
        ))}

        {isEmpty && (
          <div data-testid="rail-empty" className="flex flex-col items-center gap-2 px-5 py-10 text-center">
            {/* Fix round 2 (finding 12): was `bg-elevated` — wrong slot for
                card-level content, per tokens.css's own rule (finding 1). */}
            <div className="h-[38px] w-[38px] rounded-xl bg-sunken" />
            <div className="text-[12px] font-semibold text-mid">Nothing needs you</div>
            <div className="text-[11px] leading-[1.5] text-lo">The queue is clear. Silence means silence.</div>
          </div>
        )}
      </div>

      {/* The rail footer explained the sort rule and who else was covering, in
          a paragraph that never changed and never needed reading twice. It was
          orientation copy for a prototype nobody had used yet; a team that
          works in this all day does not need the sort order restated under
          every screen. The coverage chip in the header carries the only part
          that varies. */}
    </div>
  )
}
